package pl.nickers.match3

import kotlinx.serialization.decodeFromString
import kotlinx.serialization.encodeToString
import kotlinx.serialization.json.Json
import pl.nickers.match3.ecs.World
import pl.nickers.match3.ecs.boardEntityId

/**
 * Manages undo/redo snapshot history for the game world.
 *
 * @param world the ECS world whose snapshots are captured
 * @param afterRestore called after a snapshot is restored; receives the new board entity ID
 *   so the caller can update any cached entity references and re-sync state.
 */
internal class MoveHistory(
    private val world: World,
    private val afterRestore: (newBoardEntityId: Int) -> Unit,
) {
    private val past = ArrayDeque<WorldSnapshot>()
    private val future = ArrayDeque<WorldSnapshot>()

    val canUndo: Boolean get() = past.isNotEmpty()
    val canRedo: Boolean get() = future.isNotEmpty()

    /** Snapshot current state as a savepoint before a player move. Clears redo stack. */
    fun record() {
        future.clear()
        past.addLast(clone(world.snapshot()))
        trim(past)
    }

    /** Clear all history (e.g. after board reset or file import). */
    fun clear() {
        past.clear()
        future.clear()
    }

    /** Restore the most recent past savepoint. No-op if history is empty. */
    fun undo() {
        if (past.isEmpty()) return
        future.addLast(clone(world.snapshot()))
        trim(future)
        restore(past.removeLast())
    }

    /** Re-apply the most recently undone move. No-op if redo stack is empty. */
    fun redo() {
        if (future.isEmpty()) return
        past.addLast(clone(world.snapshot()))
        trim(past)
        restore(future.removeFirst())
    }

    private fun restore(snapshot: WorldSnapshot) {
        world.getAllEntityIds().toList().forEach { world.deleteEntity(it) }
        world.restoreFrom(snapshot)
        val boardEntityId = world.boardEntityId() ?: return
        afterRestore(boardEntityId)
    }

    private fun clone(snapshot: WorldSnapshot): WorldSnapshot =
        Json.decodeFromString(Json.encodeToString(snapshot))

    private fun trim(d: ArrayDeque<WorldSnapshot>) {
        while (d.size > MOVE_HISTORY_MAX) d.removeFirst()
    }
}
